import { Navigate } from 'react-router';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * Route guard for the OptiSound tab (`/app/optisound`).
 *
 * OptiSound is **company-wide**: locations, zones, schedules, audio messages and
 * stations belong to the whole OptiSigns account rather than to a team. Rather
 * than scope any of it per team, the whole tab is limited to **Owner / Super
 * Admin**, who already have account-wide visibility — so nothing they can reach
 * here exceeds what they can already see.
 *
 * Hiding the nav entry is not enough on its own: without this guard a member
 * could still type /app/optisound. The OptiSound API enforces the same rule
 * independently (`assertOptisignsEmbedAdmin`), so this is the affordance and
 * the API is the boundary.
 *
 * Redirects rather than showing a denial page — the signage app is still fully
 * available to these users, matching MdmFeatureRoute's optisigns path.
 */
export function OptisoundFeatureRoute({ children }: { children: React.ReactNode }) {
  const me = useAuthStore((s) => s.me);
  const { isRootSuperAdmin } = usePermissions();

  // Don't flash a redirect while auth is still loading.
  if (!me) return null;

  if (!isRootSuperAdmin) {
    return <Navigate to="/app/screenManagement" replace />;
  }

  return <>{children}</>;
}
